// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/donor.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import { createElement } from "react";
import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { StageChip } from "../../components/composition/StageChip";
import { donorsApi } from "../../api/donors";
import type { Donor } from "../../api/types";
// override-block:imports BEGIN
import { ACISurface } from "../../components/aci/ACISurface";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1325 — Tag taxonomy phase 3: Tags tab via EntityTagsSection.
import { EntityTagsSection } from "../../components/composition/EntityTagsSection";
// CHG-1665 / ITER-3774 — DonorDetail port custom_render slots.
import { LifecycleBadgeCard } from "../../components/donor/LifecycleBadgeCard";
import { AddressCard } from "../../components/donor/AddressCard";
import { AnnualReportInline } from "../../components/donor/AnnualReportInline";
// override-block:imports END

// override-block:helpers BEGIN
// override-block:helpers END

export const donorDetailConfig: DetailPageConfig<Donor> = {
  idParam: "donorId",
  queryKey: (id: string) => ["donors", "detail", id] as const,
  queryFn: (id: string) => donorsApi.getDonor(id),
  backTo: "/donors",
  // override-block:title BEGIN
  title: (r) => r.full_name,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.email ?? null,
  // override-block:subtitle END
  stageChip: (r) =>
    createElement(StageChip, {
      stage_id: r.lifecycle_stage,
      entity: "donor",
      size: "lg",
    }),
  statusCards: (r, _fmt) => [
    {
      id: "lifetime_giving",
      label: "Lifetime",
      value: r.lifetime_giving_minor_units,
      format: "currency",
      currency: r.organization_default_currency,
      tone: "g-giving",
    },
    {
      id: "ytd_giving",
      label: "YTD",
      value: r.ytd_giving_minor_units,
      format: "currency",
      currency: r.organization_default_currency,
      tone: "g-giving",
    },
    {
      id: "last_gift_at",
      label: "Last gift",
      value: r.last_gift_at,
      format: "relative-date",
      tone: "g-mygiving",
    },
    // override-block:statcard-lifecycle-badge BEGIN
    {
      id: "lifecycle_badge",
      label: "Lifecycle",
      value: null,
      format: "text",
      tone: "g-people",
      // CHG-1665 — projects donor.lifecycle_stage + per-stage blurb.
      render: createElement(LifecycleBadgeCard, { donor: r }),
    },
    // override-block:statcard-lifecycle-badge END
    {
      id: "risk_score",
      label: "Risk score",
      value: r.engagement_score,
      format: "number",
      tone: "g-events",
    },
  ],
  sections: [
    // override-block:section-address-card BEGIN
    {
      id: "address-card",
      // CHG-1665 — mailing address (4-field projection + empty state).
      render: (d) => createElement(AddressCard, { donor: d }),
    },
    // override-block:section-address-card END
    // override-block:section-annual-report-inline BEGIN
    {
      id: "annual-report-inline",
      // CHG-1665 — annual_report_opt_in tristate badge (read-only;
      // toggle deferred to follow-up CHG).
      render: (d) => createElement(AnnualReportInline, { donor: d }),
    },
    // override-block:section-annual-report-inline END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.donor_detail["edit"], "disable") },
    { id: "record-donation", label: "Record donation", variant: "primary", permissionGate: toGate(GATE_CATALOG.donor_detail["record-donation"], "hide") },
    { id: "record-pledge", label: "Record pledge", variant: "ghost", permissionGate: toGate(GATE_CATALOG.donor_detail["record-pledge"], "hide") },
    // CHG-1665 — merge promoted to header per the DonorDetail port brief.
    // onClick wired by the page wrapper (apps/dashboard-v2/src/pages/
    // DonorDetail.tsx) which composes this config + overlays a merge
    // modal; the wrapper substitutes the actual onClick when it spreads
    // donorDetailConfig.actions(r). Permission gate uses donor.merge
    // (forward-promised in GATE_CATALOG); falls back to hide when
    // catalog entry isn't present.
    { id: "merge", label: "Merge donor", variant: "ghost" },
    // override-block:actions END
  ],
  tabs: [
    // override-block:tab-overview BEGIN
    {
      id: "overview",
      label: "Overview",
      // CHG-1665 — migrated from the pre-existing custom-tabs block.
      render: (d, fmt) =>
        createElement("div", { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Member since: ", fmt.formatDate(d.created_at, "long")),
          createElement("div", null, "Last gift: ", d.last_gift_at ? fmt.formatDate(d.last_gift_at, "relative") : "—"),
          createElement("div", null, "Status: ", d.status),
        ),
    },
    // override-block:tab-overview END
    // override-block:tab-donations BEGIN
    {
      id: "donations",
      label: "Donations",
      render: (d) =>
        createElement("div", { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Donation timeline placeholder — fetched via /donors/${d.id}/timeline. Full UI in ε CHG.`,
        ),
    },
    // override-block:tab-donations END
    // override-block:tab-recurring BEGIN
    {
      id: "recurring",
      label: "Recurring",
      render: (d) =>
        createElement("div", { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Recurring donations — fetched via /donors/${d.id}/recurring. Full UI in ε CHG.`,
        ),
    },
    // override-block:tab-recurring END
    // override-block:tab-pledges BEGIN
    {
      id: "pledges",
      label: "Pledges",
      render: (d) =>
        createElement("div", { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Pledges — fetched via /donors/${d.id}/pledges. Full UI in ε CHG.`,
        ),
    },
    // override-block:tab-pledges END
    // override-block:tab-communications BEGIN
    {
      id: "communications",
      label: "Communications",
      render: (d) =>
        createElement("div", { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Delivery logs — fetched via /donors/${d.id}/delivery-logs. Full UI in ε CHG.`,
        ),
    },
    // override-block:tab-communications END
    // override-block:tab-engagement BEGIN
    {
      id: "engagement",
      label: "Engagement",
      // CHG-1665 — new tab. Pulls engagement_score + activity proxies.
      // Wires to a per-donor engagement metric stream in a follow-up CHG;
      // for the Tier-1 minimum surfaces the engagement_score number +
      // a link to the donations tab.
      render: (d) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Engagement score: ", String(d.engagement_score ?? "—")),
          createElement(
            "div",
            { style: { color: "var(--color-text-muted)" } },
            "Detailed engagement timeline + outreach history lands in a follow-up CHG.",
          ),
        ),
    },
    // override-block:tab-engagement END
    // override-block:tab-notes BEGIN
    {
      id: "notes",
      label: "Notes",
      render: (d) =>
        createElement(
          "div",
          { style: { display: "flex", flexDirection: "column", gap: 16 } },
          createElement("div", { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
            `Communication preferences (notes endpoint placeholder) — fetched via /donors/${d.id}/preferences.`,
          ),
          // CHG-1274 — ACI runtime surface 3 (donor-aci-notes).
          createElement(ACISurface, {
            surface_id: "donor-aci-notes",
            entity_id: d.id,
          }),
        ),
    },
    // override-block:tab-notes END
    // override-block:custom-tabs BEGIN
    // CHG-1325 — Tag taxonomy phase 3. Renders the tags applied to
    // this donor + a TagPicker for attach/detach + inline create.
    // Permission-gated on tag.read inside EntityTagsSection. Not in
    // the entity-hint tab list (tags surface is cross-cutting, added
    // by the tag taxonomy CHG), so it lives in custom-tabs.
    {
      id: "tags",
      label: "Tags",
      render: (d) =>
        createElement(EntityTagsSection, {
          entityType: "donor",
          entityId: d.id,
        }),
    },
    // override-block:custom-tabs END
  ],
};
